/**
 * The two-phase content fit of an agent presentation (Part 05 decision 12).
 * The overlay opens focus before the frame's body exists, so the fit starts
 * from the natural size or the requested one. Once the focus window's body
 * reports its content, `refitAgentContent` measures it and reflows the
 * window with no transition: once per presentation, within 1.5 s, and never
 * after the user moved, resized or maximised the window.
 */
import {
  findMountedFrameElement,
  frameContentReported,
  frameMeasureContent,
  measureFramePresentationContent,
  measureScaledContentAspect,
} from "@/desktop/frame-presentation-measurement";
import { currentFramePresentationSurface } from "@/desktop/frame-presentation-surface";
import type { DesiredContentSize } from "@/lib/canvas-presentation-types";
import {
  agentContentFitBox,
  reflowFocusPresentationLayout,
} from "@/lib/focus-presentation-reflow";
import { calculateNormalFocusWindowBox } from "@/lib/focus-window-layout";
import { frameNaturalSize } from "@/lib/focus/focus-natural-size";
import { focusBoxesMatch } from "@/lib/focus/focus-plan-layout";
import type {
  FocusNaturalSize,
  PresentationState,
} from "@/lib/focus/focus-types";
import { useFramesStore } from "@/stores/frames-store";
import { usePresentationStore } from "@/stores/presentation-store";

type AgentContentFit = NonNullable<PresentationState["agentContentFit"]>;

const REFIT_WINDOW_MS = 1_500;

interface ArmedRefit {
  frameId: string;
  requested: DesiredContentSize | undefined;
  expiresAt: number;
}

let armed: ArmedRefit | null = null;

function frameTypeOf(frameId: string): string | undefined {
  return useFramesStore.getState().framesById[frameId]?.frameType;
}

interface MeasuredContent {
  desired: AgentContentFit["desired"];
  mounted: boolean;
}

/**
 * An object frame's content fit for a requested size. Its type scales with
 * its width, so a box of another shape crops it: it keeps its content's
 * aspect, fitted within the requested box. The aspect is the mounted body's,
 * else its card's.
 */
function measureObjectContent(
  frameId: string,
  fallback: FocusNaturalSize,
  requested: DesiredContentSize,
): MeasuredContent {
  const aspectRatio =
    measureScaledContentAspect(frameId) ?? fallback.width / fallback.height;
  const within = Math.min(
    requested.width ?? Number.POSITIVE_INFINITY,
    (requested.height ?? Number.POSITIVE_INFINITY) * aspectRatio,
  );
  const width = Number.isFinite(within) ? within : fallback.width;
  return {
    mounted: findMountedFrameElement(frameId) !== null,
    desired: { width, height: width / aspectRatio, aspectRatio },
  };
}

/**
 * The size a content fit asks for: the mounted body's content (or the
 * requested size), at least the readable focus default unless the agent
 * asked for a size or the frame opens at a final size (its card's shape, a
 * compact prompt's content). An object frame
 * keeps its content's aspect (`measureObjectContent`).
 */
export function measureAgentContent(
  frameId: string,
  requested: DesiredContentSize | undefined,
  fitToDisplay: boolean | undefined,
): MeasuredContent {
  const frameType = frameTypeOf(frameId);
  const fallback = frameNaturalSize(frameId);
  // Asked for a size: keep the content's aspect within it, never crop.
  // Unasked, it opens at its card's shape like any frame.
  if (fallback.container === "object" && requested) {
    return measureObjectContent(frameId, fallback, requested);
  }
  const measured = measureFramePresentationContent(
    frameId,
    fallback,
    frameType,
    requested,
  );
  const surface = currentFramePresentationSurface(fitToDisplay);
  const normalBox = calculateNormalFocusWindowBox({
    x: surface.left,
    y: surface.top,
    width: surface.width,
    height: surface.height,
  });
  // A tool benefits from a readable default. A media, reading or compact
  // frame opens at a final size: it is never enlarged, only grown to fit
  // its measured content.
  const useReadableDefault =
    !requested && (fallback.container ?? "normal") === "normal";
  return {
    mounted: measured.mounted,
    desired: {
      width: Math.max(
        measured.width || fallback.width,
        useReadableDefault ? normalBox.width : 0,
      ),
      height: Math.max(
        measured.height || fallback.height,
        useReadableDefault ? normalBox.height : 0,
      ),
      aspectRatio: measured.aspectRatio,
    },
  };
}

/** The presentation just entered fitted `frameId` before its body mounted. */
export function armAgentContentRefit(
  frameId: string,
  requested: DesiredContentSize | undefined,
): void {
  armed = { frameId, requested, expiresAt: Date.now() + REFIT_WINDOW_MS };
}

/** The window still shows the content fit's box: nobody moved it since. */
function showsContentFit(presentation: PresentationState): boolean {
  const fitBox = agentContentFitBox(presentation);
  const box = presentation.boxes[0];
  return !!fitBox && !!box && focusBoxesMatch(box, fitBox);
}

/** Measure the mounted focus window again and reflow it to its content. */
export function refitAgentContent(frameId: string): boolean {
  const entry = armed;
  if (entry?.frameId !== frameId) return false;
  const presentation = usePresentationStore.getState().presentation;
  const fit = presentation?.agentContentFit;
  if (
    Date.now() > entry.expiresAt ||
    presentation?.variant !== "focus" ||
    fit?.frameId !== frameId ||
    !showsContentFit(presentation)
  ) {
    armed = null;
    return false;
  }
  const { desired, mounted } = measureAgentContent(
    frameId,
    entry.requested,
    fit.fitToDisplay,
  );
  if (!mounted) return false;
  armed = null;
  usePresentationStore.setState({
    presentation: { ...presentation, agentContentFit: { ...fit, desired } },
  });
  return reflowFocusPresentationLayout({ duration: 0 });
}

/**
 * A focus window's body mounted: refit once it reports its content (an
 * image's load, an HTML document's size, content laid out to a size). The
 * watch ends with the refit window.
 */
export function watchAgentContentRefit(frameId: string, root: Element): void {
  const entry = armed;
  if (entry?.frameId !== frameId) return;
  const frameType = frameTypeOf(frameId);
  let watching = true;
  let resizedContent: Element | null = null;
  const mutations = new MutationObserver(check);
  const resizes =
    typeof ResizeObserver === "undefined" ? null : new ResizeObserver(check);
  const timer = setTimeout(stop, Math.max(0, entry.expiresAt - Date.now()));
  function stop() {
    if (!watching) return;
    watching = false;
    clearTimeout(timer);
    mutations.disconnect();
    resizes?.disconnect();
    root.removeEventListener("load", check, true);
    root.removeEventListener("loadedmetadata", check, true);
  }
  function check() {
    if (!watching) return;
    if (armed !== entry) {
      stop();
      return;
    }
    // Content lays out after it mounts: its first size is a report.
    const content = frameMeasureContent(root);
    if (resizes && content && content !== resizedContent) {
      if (resizedContent) resizes.unobserve(resizedContent);
      resizedContent = content;
      resizes.observe(content);
    }
    if (!frameContentReported(root, frameType)) return;
    stop();
    refitAgentContent(frameId);
  }
  root.addEventListener("load", check, true);
  root.addEventListener("loadedmetadata", check, true);
  mutations.observe(root, {
    subtree: true,
    childList: true,
    attributeFilter: ["data-content-width", "data-content-height"],
  });
  check();
}
